import React, { createContext, useMemo, useState, useCallback, useEffect } from 'react'
import { getCurrentLocale, getDefaultLoggedOutSalesOrg } from '@insight/toolkit-utils/lib/helpers'
import { initCommerceEngine, initContentEngine } from './engines'
import { ILISTVIEW, INSIGHT_CURRENT_LOCALE_COOKIE_NAME, CONTENTMAP,
  INSIGHT_LOCALE_COOKIE_NAME, SEARCH_REDIRECT_FLAG, LISTING_PAGE_FLAG,
  IN_STOCK_STATUS_FIELD } from '../constants'
import {
  getSortString,
  mapCoveoFacets,
  mapBreadcrumbsToFilterMap,
  mapResultToProduct,
  resolveIsListingPage,
} from '../shared/coveoUtils'
import { usePriceData } from '../hooks/usePriceData'
import { useControllerState } from '../hooks/useControllerState'
import { useUrlSync } from '../hooks/useUrlSync'
import { useProductSearch } from '../hooks/useProductSearch'
import { useContentSearch } from '../hooks/useContentSearch'
import { useSearchAnalytics } from '../hooks/useSearchAnalytics'
import { createStubCommerceInstance, createStubContentInstance, createStubRedirectTrigger } from './DefaultSearchContext'
import { useSingleResultRedirect } from '../hooks/useSingleResultRedirect'
const locale = getCurrentLocale(INSIGHT_CURRENT_LOCALE_COOKIE_NAME)
const insightLocale = getCurrentLocale(INSIGHT_LOCALE_COOKIE_NAME)

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
export const SearchContext = createContext({})

export function SearchProvider({
  children,
  salesOrg: contextSalesOrg,
  coveoData,
  currencyCode,
  contract,
  isLoggedIn,
  isIPSUser,
  permissions,
  userType,
  initialIsListingPage = false,
}) {
  const entitlementQuery = coveoData?.entitlementQuery
  // Lazy-init engines + controllers (idempotent — returns cached instances)
  // If an engine failed to initialize, stub it so the rest of the provider
  // can proceed without null-reference errors.
  const salesOrg = contextSalesOrg || getDefaultLoggedOutSalesOrg(locale, isIPSUser) || '2400'
  const commerceInstance = initCommerceEngine(coveoData, locale, currencyCode, salesOrg, userType) || createStubCommerceInstance();
  const contentInstance = initContentEngine(coveoData) || createStubContentInstance();

  const {
    engine,
    searchBox,
    didYouMean,
    cart,
    controllers,
  } = commerceInstance;

  const {
    engine: contentEngine,
    searchBox: contentSearchBox,
    resultList: contentResultList,
    pager: contentPager,
    resultsPerPage: contentResultsPerPage,
    contentFacets,
    facetGenerator: contentFacetGenerator,
    breadcrumbManager: contentBreadcrumbManager,
    didYouMean: contentDidYouMean,
    summary: contentSummary,
    urlManager: contentUrlManager,
  } = contentInstance;

  const isListingPage = useMemo(() => {
    const shouldTreatAsListing = resolveIsListingPage({ initialIsListingPage, insightLocale })
    if (shouldTreatAsListing && typeof window !== 'undefined') {
      window[LISTING_PAGE_FLAG] = true
    }
    return shouldTreatAsListing
  }, [initialIsListingPage])

  const pageType            = isListingPage ? 'listing' : 'search'
  const activeControllers   = controllers[pageType]
  const activeCore          = activeControllers.core
  const activeSort          = activeControllers.sort
  const activePagination    = activeControllers.pagination
  const activeUrlManager    = activeControllers.urlManager
  const activeFacetGenerator = activeControllers.facetGenerator

  // ─── Controller state subscriptions ─────────────────────────────────────
  const searchState     = useControllerState(activeCore)
  const sortState       = useControllerState(activeSort)
  const paginationState = useControllerState(activePagination)
  const didYouMeanState = useControllerState(didYouMean)
  const summaryState    = useControllerState(activeControllers.summary)
  const breadcrumbState = useControllerState(activeControllers.breadcrumb)
  const redirectTriggerState = useControllerState(
    controllers.search.redirectTrigger || createStubRedirectTrigger()
  )

  // ─── URL sync + first search execution ──────────────────────────────────
  useUrlSync({
    activeCore,
    activeUrlManager,
    activeFacetGenerator,
    contentFacets,
    contentFacetGenerator,
    contentUrlManager,
    contentBreadcrumbManager,
    contentSearchBox,
    isListingPage,
  })

  // ─── UI state (not managed by Coveo) ────────────────────────────────────
  const gridViewDefault = typeof window !== 'undefined' && window?.flags?.['GNA-17775_GRID_VIEW_SEARCH_RESULT']
  const [view,    setView]    = useState(gridViewDefault ? ILISTVIEW.grid : ILISTVIEW.list)
  const [tabType, setTabtype] = useState(() => {
    if (typeof window === 'undefined') return CONTENTMAP.products.id
    return new URLSearchParams(window?.location?.search).get('tabType') || CONTENTMAP.products.id
  })
  const [contents, setContents] = useState({})

  // ─── Derived product state ───────────────────────────────────────────────
  const products = useMemo(
    () => (searchState.results || searchState.products || [])
      .filter((r) => r.resultType !== 'spotlight')
      .map(mapResultToProduct),
    [searchState.results, searchState.products]
  )

  const indexPriceInfo = useMemo(() => {
    const info = {}
    products.forEach((p) => {
      info[p.materialId] = {
        listPrice:       p.price.listPrice,
        listPriceInclVat: p.price.listPriceInclVat,
        webPrice:        p.price.webPrice,
        webPriceInclVat: p.price.webPriceInclVat,
        indexPrice:      p.price.indexPrice,
        displayDiscount: p.price.displayDiscount,
        priceLabel:      p.price.priceLabel,
        callForPrice:    p.callForPrice ?? false,
        proratable:      p.proratable ?? false,
        unlimited:       p.unlimited ?? false,
        stock:           p.regularStock,
      }
    })
    return info
  }, [products])

  const contractId = contract?.contractNumber
  const contractType = contract?.contractName
  const { priceInfo: ssePrices, searchSSEComplete, searchSSEError } = usePriceData(products, { salesOrg, contractId, contractType })

  // Only logged-in and IPS users take SSE pricing; everyone else keeps Coveo index prices.
  const useSSEPricing = Boolean(isLoggedIn || isIPSUser)

  const priceInfo = useMemo(() => {
    if (!ssePrices || !Object.keys(ssePrices).length) return indexPriceInfo
    const merged = { ...indexPriceInfo }
    const ssePriceList = Array.isArray(ssePrices)
      ? ssePrices
      : Array.isArray(ssePrices?.prices)
        ? ssePrices.prices
        : null
    // unlimited (and other fields like regularStock) live on the item itself, not inside item.price
    const sseEntries = Array.isArray(ssePriceList)
      ? ssePriceList
        .map((item) => [item?.materialId, item ? { ...item, ...item.price } : item])
        .filter(([materialId, ssePrice]) => Boolean(materialId) && ssePrice != null)
      : Object.entries(ssePrices)
        .map(([materialId, item]) => [materialId, item ? { ...item, ...item.price } : item])
        .filter(([materialId, ssePrice]) => Boolean(materialId) && ssePrice != null)

    sseEntries.forEach(([materialId, ssePrice]) => {
      const base = indexPriceInfo[materialId] || {}

      // callForPrice, proratable and unlimited must come from SSE pricing for all user types (default false)
      // Coveo does not have callForPrice in the response
      // additionalFields.inp_call_for_price will not return in coveo's response, so we're getting it for SSE Pricing API
      if (!useSSEPricing) {
        merged[materialId] = {
          ...base,
          callForPrice: ssePrice.callForPrice ?? base.callForPrice ?? false,
          proratable:   ssePrice.proratable ?? base.proratable ?? false,
          unlimited:    ssePrice.unlimited ?? base.unlimited ?? false,
          stock:        ssePrice.regularStock ?? 0,
        }
        return
      }

      merged[materialId] = {
        ...base,
        webPrice:        ssePrice.webPrice  ?? base.webPrice,
        listPrice:       ssePrice.listPrice ?? base.listPrice,
        webPriceInclVat: ssePrice.webPriceInclVat ?? base.webPriceInclVat,
        listPriceInclVat: ssePrice.listPriceInclVat ?? base.listPriceInclVat,
        // presence check keeps an SSE-provided 0 or null instead of falling back to the index value
        indexPrice:      ssePrice !== undefined ? ssePrice.indexPrice : base.indexPrice,
        priceLabel:      ssePrice.priceLabel ?? base.priceLabel,
        displayDiscount: ssePrice.webPrice != null && ssePrice.listPrice != null
          ? ssePrice.webPrice < ssePrice.listPrice
          : base.displayDiscount,
        callForPrice:    ssePrice.callForPrice ?? base.callForPrice ?? false,
        proratable:      ssePrice.proratable   ?? base.proratable ?? false,
        unlimited:       ssePrice.unlimited    ?? base.unlimited  ?? false,
        ...(ssePrice.abbreviation != null ? { abbreviation: ssePrice.abbreviation } : {}),
        ...(ssePrice.contractName != null ? { contractName: ssePrice.contractName } : {}),
        ...(ssePrice.contractNumber != null ? { contractNumber: ssePrice.contractNumber } : {}),
        ...(ssePrice.additionalPrices != null ? { additionalPrices: ssePrice.additionalPrices } : {}),
        stock:           ssePrice.regularStock ?? 0,
      }
    })
    return merged
  }, [indexPriceInfo, ssePrices, useSSEPricing])

  const facets = useMemo(
    () => mapCoveoFacets(activeFacetGenerator.facets),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFacetGenerator.facets]
  )

  // ─── Derive instockOnly state from IN_STOCK_STATUS_FIELD facet ───
  // When the 'true' value is selected on the IN_STOCK_STATUS_FIELD field,
  // instockOnly should be true (show in stock only)
  const instockOnly = useMemo(() => {
    const inStockFacet = activeFacetGenerator.facets.find(
      (f) => f.state.field === IN_STOCK_STATUS_FIELD
    )
    if (!inStockFacet) return false
    const trueValue = inStockFacet.state.values.find(
      (v) => v.value === 'true' && v.state === 'selected'
    )
    return !!trueValue
  }, [activeFacetGenerator.facets])

  const selectedFilters = useMemo(
    () => mapBreadcrumbsToFilterMap(breadcrumbState.facetBreadcrumbs),
    [breadcrumbState.facetBreadcrumbs]
  )
  const originalFilters = selectedFilters

  const numFound                = summaryState.totalNumberOfProducts
  const rows                    = paginationState.pageSize
  const pageNumber              = paginationState.page + 1
  const pages                   = paginationState.totalPages
  const start                   = paginationState.page * rows
  const isSearching             = summaryState.isLoading
  const entitlementQueryFailed  = entitlementQuery === null
  const error = useMemo(
    () => searchState.error || (entitlementQueryFailed ? new Error('build-query-failed') : null),
    [searchState.error, entitlementQueryFailed]
  )
  const firstSearchExecuted     = summaryState.firstRequestExecuted || entitlementQueryFailed
  const noResultsFound          = firstSearchExecuted && numFound === 0 && !isSearching
  const correctedSpelling       = didYouMeanState.wasCorrectedTo || ''
  const originalSpelling        = didYouMeanState.originalQuery  || ''
  const customSort              = useMemo(() => getSortString(sortState), [sortState])
  const origParams              = useMemo(
    () => ({ q: summaryState.query || '' }),
    [summaryState.query]
  )

  // ─── Product search actions ──────────────────────────────────────────────
  const { redirectAndTrack, searchProducts, searchHandler, setFilter, clearFilters } = useProductSearch({
    activeCore, activeSort, activePagination, activeFacetGenerator, activeUrlManager, searchBox, isListingPage, isLoggedIn,
  })
  
  const REDIRECT_FLAG = typeof window !== 'undefined' && window?.flags?.[SEARCH_REDIRECT_FLAG]
  const searchRedirectEnabled = REDIRECT_FLAG && (!permissions?.disableSearchRedirect || !isLoggedIn)

  useEffect(() => {
    if (redirectTriggerState.redirectTo && searchRedirectEnabled) redirectAndTrack(redirectTriggerState.redirectTo)
  }, [redirectTriggerState.redirectTo])

  // ─── PDP Redirection for single product result ───────────────────────────
  useSingleResultRedirect({ tabType, firstSearchExecuted, isSearching, products })

  // ─── Content search state + actions ─────────────────────────────────────
  const {
    nextContentTab,
    solutionsServicesDocs,
    searchBrands,
    searchArticles,
    searchSolutions,
    searchSolutionsAndServices,
    contentSearchHandler,
    setContentFilter,
    clearContentFilters,
    searchDocs,
  } = useContentSearch({
    contentEngine, contentSearchBox, contentPager, contentResultsPerPage,
    contentFacets,
    contentFacetGenerator, contentBreadcrumbManager, contentSummary,
    contentResultList, contentDidYouMean, locale,
  })

  // ─── Dispatch ────────────────────────────────────────────────────────────
  const dispatch = useCallback(({ type, payload }) => {
    switch (type) {
      case 'SET_PRODUCT_VIEW':
        setView(payload?.view ?? view)
        break
      case 'RESET_PAGE':
        if (payload?.tabType === CONTENTMAP.solutionsAndServices.id) {
          contentPager.selectPage(1)
        }
        break
      case 'FETCH_CONTENTS': {
        const { solutionsAndServices: _ignored, ...rest } = payload || {}
        setContents(rest)
        break
      }
      case 'UPDATE_CONTENT_FILTERS':
        if (payload?.clearAllFilters) {
          contentBreadcrumbManager.deselectAll()
        }
        break
      default:
        break
    }
  }, [contentBreadcrumbManager, contentPager, view])

  const effectiveContentData = useMemo(
    () => ({ [CONTENTMAP.solutionsAndServices.id]: nextContentTab }),
    [nextContentTab]
  )

  // ─── Full-page "No results" decision (GNA-22034) ─────────────────────────
  // Show the standalone SearchNotFound page only when products AND all content
  // tabs are empty (matches app-search / Lucidworks). Wait until content counts
  // are resolved so SearchPage (which fetches them) stays mounted meanwhile.
  // NOTE: noResultsFound is intentionally unchanged — ProductsTab relies on it.
  const contentsFetched = Object.keys(contents || {}).length > 0
  const contentSearching = !!nextContentTab?.isSearching
  const hasContentResults =
    (nextContentTab?.numFound || 0) > 0 ||
    Object.values(contents || {}).some((c) => (c?.total || c?.numFound || 0) > 0)

  // Latch "content resolved" per query. SearchNotFound re-runs the S&S search
  // (searchDocs) on mount; without this latch, that in-flight search would flip
  // showFullNotFound back to false → remount SearchPage → infinite loop.
  const currentQuery = summaryState.query || ''
  const [contentResolvedQuery, setContentResolvedQuery] = useState(null)
  useEffect(() => {
    if (contentsFetched && !contentSearching) setContentResolvedQuery(currentQuery)
  }, [contentsFetched, contentSearching, currentQuery])
  const contentResolved = contentResolvedQuery === currentQuery

  const showFullNotFound =
    noResultsFound && !error && contentResolved && !hasContentResults

  const contentSelectedFilters = useMemo(
    () => mapBreadcrumbsToFilterMap(
      (nextContentTab?.breadcrumbs || []).map((breadcrumb) => ({
        ...breadcrumb,
        field: breadcrumb.facetId ?? breadcrumb.field,
      }))
    ),
    [nextContentTab?.breadcrumbs]
  )

  // ─── Analytics ───────────────────────────────────────────────────────────
  const { trackSearchTabsData, trackProductClick, trackAddToCart } = useSearchAnalytics({
    activeCore, cart, facets, selectedFilters, numFound, origParams, contents, effectiveContentData,
    docs: solutionsServicesDocs, contentSelectedFilters,
  })

  // ─── Context value ───────────────────────────────────────────────────────
  const value = {
    products,
    numFound,
    start,
    rows,
    pages,
    pageNumber,
    isSearching,
    firstSearchExecuted,
    error,
    facets,
    selectedFilters,
    originalFilters,
    contentSelectedFilters,
    correctedSpelling,
    originalSpelling,
    customSort,
    origParams,
    noResultsFound,
    showFullNotFound,
    query: summaryState.query || searchBox.state.value || '',

    priceInfo,
    ssePrices,

    fusionQueryId:     null,
    origFacetDisplays: null,
    searchSource:      'coveo',
    salesOrg,
    signalMetaData:    null,
    searchSSEComplete,
    searchSSEError,
    currency:          currencyCode || 'USD',

    view,
    tabType,
    instockOnly,
    outOfStockOnly: false,

    contents,
    contentData: effectiveContentData,
    docs: solutionsServicesDocs,

    searchProducts,
    searchHandler,
    setFilter,
    clearFilters,
    dispatch,

    searchBrands,
    searchArticles,
    searchSolutions,
    searchSolutionsAndServices,
    searchDocs,
    contentSearchHandler,
    setContentFilter,
    clearContentFilters,
    setTabtype,

    trackSearchTabsData,
    redirectAndTrack,
    trackProductClick,
    trackAddToCart,

    coveoEngine:          engine,
    coveoContentEngine:   contentEngine,
    facetGenerator:       activeFacetGenerator,
    paginationController: activePagination,
  }

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  )
}

